import { apiService } from '../../services/api';
/* ===== src/components/BookPreview.tsx ===== */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { ProjectState } from '../types/project';
import type { Chapter } from '../types/core';
import { getProjectTitle } from '../utils/projectUtils';
import { generateAboutTheAuthorSection, generateContactSocialMediaSection } from '../utils/authorUtils';
import { generatePdf } from '../utils/generatePdf';
import { generateEpub } from '../utils/generateEpub';
import { generateAndDownloadKindleEpub } from '../utils/generateKindleEpub';
import { markdownToHtml } from '../utils/markdownToHtml';
import { getChapterContent } from '../utils/projectStateUtils';
import { calculateBookPageStructure, generateTableOfContents } from '../utils/pageCalculation';
import { projectStateToMarkdown } from '../utils/bookExport';
import { useAppContext } from '../contexts/AppContext'; // Keep if other context values are used
import { useQuery } from '@tanstack/react-query';
import { cacheManager } from '../../utils/cacheUtils';
import { CoverImageWithFallback } from './CoverImageWithFallback';

interface BookPreviewProps {
    projectState: ProjectState;
}

const BookPreview: React.FC<BookPreviewProps> = ({ projectState }) => {
  // If handleExportChapterContentPreferenceChange is still needed from context, keep useAppContext
  const { handleExportChapterContentPreferenceChange, dbProject } = useAppContext();

  // CRITICAL FIX: Fetch complete project data including book structure
  const projectId = projectState?.projectId || '';
  const { data: summaryData } = useQuery({
    queryKey: ['story-project-summary', projectId],
    queryFn: async () => {
      return await apiService.get(`/api/story-projects/${projectId}/summary`);
    },
    enabled: !!projectId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes (extended to prevent refetch after blueprint generation)
    refetchOnMount: false,     // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Merge project state with summary data to get complete book structure data
  const enhancedProjectState = useMemo(() => {
    if (!projectState) return projectState;
    
    const enhanced = {
      ...projectState,
      // Merge with summary data for complete book structure
      authorDetails: {
        ...projectState.authorDetails,
        ...summaryData?.authorDetails,
      },
      frontMatter: {
        ...projectState.frontMatter,
        ...summaryData?.frontMatter,
      },
      backMatter: {
        ...projectState.backMatter,
        ...summaryData?.backMatter,
      },
    };

    return enhanced;
  }, [projectState, summaryData]);

  // CRITICAL DEBUG: Log book structure data availability for troubleshooting
  useEffect(() => {
}, [projectState, summaryData, enhancedProjectState]); 

  const [exportFormat, setExportFormat] = useState<'PDF' | 'EPUB' | 'KINDLE' | 'BOTH'>('PDF');
  const [isExporting, setIsExporting] = useState(false);
  
  // Default selections structure
  const getDefaultSections = () => ({
    frontMatter: {
      enabled: true,
      titlePageContent: true,
      copyrightPageContent: true,
      dedication: true,
      epigraph: true,
      tableOfContents: true,
      foreword: true,
      preface: true,
      acknowledgmentsFront: true,
      prologue: true,
      introduction: true
    },
    chapters: {
      enabled: true
    },
    backMatter: {
      enabled: true,
      epilogue: true,
      afterword: true,
      authorsNote: true,
      acknowledgmentsBack: true,
      appendices: true,
      glossary: true,
      bibliography: true,
      index: true,
      readingGroupGuide: true,
      teaserNextBook: true,
      aboutTheAuthor: true,
      contactSocialMedia: true
    }
  });

  // Initialize section selections from backend via summaryData or use defaults
  const [selectedSections, setSelectedSections] = useState(getDefaultSections);
  const [includeBackground, setIncludeBackground] = useState(true);
  const [isSavingSections, setIsSavingSections] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    frontMatter: true,
    backMatter: true
  });
  
  // Track if sections have been loaded from backend to prevent unnecessary saves
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const lastSavedSelections = useRef<string>('');

  // Debounced save function to avoid excessive API calls - MUST be defined BEFORE useEffects that use it
  const saveSectionSelections = useCallback(async (selections: any) => {
    if (isSavingSections) return;

    setIsSavingSections(true);
    try {
      
      await apiService.put('/api/user/section-preferences', { selections });

      // Update the last saved reference to prevent unnecessary saves
      lastSavedSelections.current = JSON.stringify(selections);
    } catch (error) {
      console.error('❌ [USER-PREFERENCES] Failed to save section preferences:', error);
      // Fallback: save to localStorage if backend fails
      if (projectState?.projectId) {
        localStorage.setItem(`bookPreview_sections_${projectState.projectId}`, JSON.stringify(selections));
      }
    } finally {
      setIsSavingSections(false);
    }
  }, [isSavingSections, projectState?.projectId]);

  // Fetch user's section preferences when component loads
  useEffect(() => {
    const fetchUserSectionPreferences = async () => {
      try {
        
        const data = await apiService.get('/api/user/section-preferences');
        
        if (data.sectionSelections) {
          setSelectedSections(data.sectionSelections);
          lastSavedSelections.current = JSON.stringify(data.sectionSelections);
        } else {
          // Fallback: try to migrate from localStorage if no backend data exists
          const projectId = projectState?.projectId;
          if (projectId) {
            const stored = localStorage.getItem(`bookPreview_sections_${projectId}`);
            if (stored) {
              try {
                const parsedSections = JSON.parse(stored);
                setSelectedSections(parsedSections);
                lastSavedSelections.current = JSON.stringify(parsedSections);
                // Clean up old localStorage immediately
                localStorage.removeItem(`bookPreview_sections_${projectId}`);
                // Save to backend asynchronously (without awaiting to avoid circular dependency)
                apiService.put('/api/user/section-preferences', { selections: parsedSections })
                  .catch(err => console.error('Migration save failed:', err));
              } catch (e) {
                console.warn('Failed to parse stored sections from localStorage:', e);
              }
            }
          }
        }
        setSectionsLoaded(true);
      } catch (error) {
        console.error('❌ [USER-PREFERENCES] Failed to fetch section preferences:', error);
        // Use default selections if fetching fails
        setSelectedSections(getDefaultSections());
      }
    };

    fetchUserSectionPreferences();
    // Remove saveSectionSelections from dependencies to avoid circular dependency
  }, [projectState?.projectId]);

  // Helper function to check if a section has content (is generated)
  const hasContent = (section: 'frontMatter' | 'backMatter', key: string): boolean => {
    const sectionData = enhancedProjectState?.[section];
    if (!sectionData) return false;

    const value = sectionData[key as keyof typeof sectionData];
    return !!(value && String(value).trim() !== '');
  };

  // Helper function to check if author details field has content
  const hasAuthorDetailsContent = (key: string): boolean => {
    const authorDetails = enhancedProjectState?.authorDetails;
    if (!authorDetails) return false;

    const value = authorDetails[key as keyof typeof authorDetails];
    return !!(value && String(value).trim() !== '');
  };

  // Get available front matter sections (only those with content)
  const getAvailableFrontMatterSections = () => {
    const allSections = [
      { key: 'titlePageContent', label: 'Title Page' },
      { key: 'copyrightPageContent', label: 'Copyright Page' },
      { key: 'dedication', label: 'Dedication' },
      { key: 'epigraph', label: 'Epigraph' },
      { key: 'tableOfContents', label: 'Table of Contents' },
      { key: 'foreword', label: 'Foreword' },
      { key: 'preface', label: 'Preface' },
      { key: 'acknowledgmentsFront', label: 'Acknowledgments' },
      { key: 'prologue', label: 'Prologue' },
      { key: 'introduction', label: 'Introduction' }
    ];

    return allSections.filter(section => hasContent('frontMatter', section.key));
  };

  // Get available back matter sections (only those with content)
  const getAvailableBackMatterSections = () => {
    const allSections = [
      { key: 'epilogue', label: 'Epilogue' },
      { key: 'afterword', label: 'Afterword' },
      { key: 'authorsNote', label: "Author's Note" },
      { key: 'acknowledgmentsBack', label: 'Acknowledgments' },
      { key: 'appendices', label: 'Appendices' },
      { key: 'glossary', label: 'Glossary' },
      { key: 'bibliography', label: 'Bibliography' },
      { key: 'index', label: 'Index' },
      { key: 'readingGroupGuide', label: 'Reading Guide' },
      { key: 'teaserNextBook', label: 'Next Book Teaser' },
      { key: 'aboutTheAuthor', label: 'About the Author' },
      { key: 'contactSocialMedia', label: 'Contact & Social' }
    ];

    return allSections.filter(section => hasContent('backMatter', section.key));
  };

  // Debounce section selections changes to avoid excessive API calls
  useEffect(() => {
    // Don't save if sections haven't been loaded yet (initial state)
    if (!sectionsLoaded) return;

    // Don't save if selections haven't actually changed
    const currentSelections = JSON.stringify(selectedSections);
    if (currentSelections === lastSavedSelections.current) return;

    const timeoutId = setTimeout(() => {
      saveSectionSelections(selectedSections);
      lastSavedSelections.current = currentSelections;
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [selectedSections, saveSectionSelections, sectionsLoaded]);

  // Master check/uncheck functionality
  const handleSelectAllSections = () => {
    const allSelected = {
      frontMatter: {
        enabled: true,
        titlePageContent: true,
        copyrightPageContent: true,
        dedication: true,
        epigraph: true,
        tableOfContents: true,
        foreword: true,
        preface: true,
        acknowledgmentsFront: true,
        prologue: true,
        introduction: true
      },
      chapters: {
        enabled: true
      },
      backMatter: {
        enabled: true,
        epilogue: true,
        afterword: true,
        authorsNote: true,
        acknowledgmentsBack: true,
        appendices: true,
        glossary: true,
        bibliography: true,
        index: true,
        readingGroupGuide: true,
        teaserNextBook: true,
        aboutTheAuthor: true,
        contactSocialMedia: true
      }
    };
    setSelectedSections(allSelected);
  };

  const handleDeselectAllSections = () => {
    const allDeselected = {
      frontMatter: {
        enabled: false,
        titlePageContent: false,
        copyrightPageContent: false,
        dedication: false,
        epigraph: false,
        tableOfContents: false,
        foreword: false,
        preface: false,
        acknowledgmentsFront: false,
        prologue: false,
        introduction: false
      },
      chapters: {
        enabled: false
      },
      backMatter: {
        enabled: false,
        epilogue: false,
        afterword: false,
        authorsNote: false,
        acknowledgmentsBack: false,
        appendices: false,
        glossary: false,
        bibliography: false,
        index: false,
        readingGroupGuide: false,
        teaserNextBook: false,
        aboutTheAuthor: false,
        contactSocialMedia: false
      }
    };
    setSelectedSections(allDeselected);
  };

  // Toggle expand/collapse for sections
  const toggleSectionExpansion = (section: 'frontMatter' | 'backMatter') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const [buildOutput, setBuildOutput] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);

  // OPTIMIZATION PHASE 2.3: Progressive chapter content loading
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [chapterFullContent, setChapterFullContent] = useState<Record<string, string>>({});
  const [loadingChapters, setLoadingChapters] = useState<Set<string>>(new Set());

  // Function to load full chapter content on demand with caching
  const loadFullChapterContent = async (chapter: Chapter) => {
    if (loadingChapters.has(chapter.id) || chapterFullContent[chapter.id]) {
      return; // Already loading or loaded
    }

    setLoadingChapters(prev => new Set(prev).add(chapter.id));

    try {
      // Check cache first
      const cached = cacheManager.getChapterContent(chapter.id);
      if (cached) {
        setChapterFullContent(prev => ({
          ...prev,
          [chapter.id]: cached.fullContent || cached.content || ''
        }));
        setExpandedChapters(prev => new Set(prev).add(chapter.id));
        return;
      }

      const data = await apiService.get(`/api/chapters/${chapter.id}/full`);
      
      // Cache the result
      cacheManager.setChapterContent(chapter.id, data);

      setChapterFullContent(prev => ({
        ...prev,
        [chapter.id]: data.fullContent || data.content || ''
      }));
      setExpandedChapters(prev => new Set(prev).add(chapter.id));
    } catch (error) {
      console.error('Error loading full chapter content:', error);
      // Fallback to existing chapter content
      const fallbackContent = getChapterContent(chapter, 'content');
      setChapterFullContent(prev => ({
        ...prev,
        [chapter.id]: fallbackContent
      }));
      setExpandedChapters(prev => new Set(prev).add(chapter.id));
    } finally {
      setLoadingChapters(prev => {
        const newSet = new Set(prev);
        newSet.delete(chapter.id);
        return newSet;
      });
    }
  };

  // Function to get chapter content with progressive loading
  const getProgressiveChapterContent = (chapter: Chapter): string => {
    // If expanded and full content is loaded, return full content
    if (expandedChapters.has(chapter.id) && chapterFullContent[chapter.id]) {
      return chapterFullContent[chapter.id];
    }

    // Otherwise, return preview content or fallback
    return chapter.previewContent || getChapterContent(chapter, 'preview') || 'No content available';
  };

  // Function to check if chapter has more content to show
  const hasMoreContent = (chapter: Chapter): boolean => {
    if (expandedChapters.has(chapter.id)) return false; // Already expanded
    
    // Check if there's full content available beyond preview
    const fullContent = getChapterContent(chapter, 'content');
    const previewContent = chapter.previewContent || '';
    
    return !!(fullContent && fullContent.length > previewContent.length);
  };

  // Comprehensive text cleaning function matching server-side utility
  const cleanTextContent = (content: string): string => {
    if (!content || typeof content !== 'string') return '';
    
    // Enhanced cleaning with markdown artifact removal
    const cleanedContent = content
      // Remove HTML tags first
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '') 
      .replace(/<\/?em>/g, '')
      .replace(/<\/?i>/g, '')
      .replace(/<\/?strong>/g, '')
      .replace(/<\/?b>/g, '')
      .replace(/<\/?mark>/g, '')
      .replace(/<\/?u>/g, '')
      .replace(/<\/?s>/g, '')
      .replace(/<\/?span[^>]*>/g, '')
      .replace(/<\/?div[^>]*>/g, '')
      .replace(/<\/?h[1-6][^>]*>/g, '')
      .replace(/<\/?ul>/g, '')
      .replace(/<\/?ol>/g, '')
      .replace(/<\/?li>/g, '')
      .replace(/<\/?br\s*\/?>/g, '\n')
      .replace(/<\/?[^>]+>/g, '') // Remove any remaining HTML tags
      
      // Remove markdown code fences and formatting
      .replace(/```markdown\n?/g, '')
      .replace(/```\n?/g, '')
      .replace(/^\s*#\s*/gm, '') // Remove markdown headers at start of lines
      
      // CRITICAL FIX: Handle markdown formatting artifacts
      .replace(/\*([^*]+)\*/g, '$1') // Remove single asterisks around text (*word* -> word)
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove double asterisks (**word** -> word)
      .replace(/_([^_]+)_/g, '$1') // Remove underscores around text (_word_ -> word)
      .replace(/__([^_]+)__/g, '$1') // Remove double underscores (__word__ -> word)
      
      // CRITICAL FIX: Convert literal \n sequences to actual paragraph breaks
      .replace(/\\n\\n/g, '\n\n') // Convert \n\n to actual double newlines
      .replace(/\\n/g, ' ') // Convert single \n to space (part of same paragraph)
      
      // Clean up spacing and normalize text
      .replace(/\s+/g, ' ') // Multiple spaces to single space
      .replace(/\n\s+/g, '\n') // Remove spaces after newlines
      .replace(/\s+\n/g, '\n') // Remove spaces before newlines
      .trim();

    return cleanedContent;
  };

  // Function to clean and render content from LLM responses
  const renderContent = (content: string) => {
      if (!content) return null;
      
      // Use the comprehensive cleaning function
      const cleanContent = cleanTextContent(content);
      
      // Split into paragraphs and filter out empty lines
      const paragraphs = cleanContent
          .split(/\n\s*\n/) // Split on double newlines
          .map(p => p.trim())
          .filter(p => p.length > 0);
      
      // Render as properly formatted paragraphs
      return paragraphs.map((paragraph, index) => (
          <p key={index} className="mb-4 text-justify leading-relaxed" 
             style={{
                 fontFamily: "'Crimson Text', 'Times New Roman', serif", 
                 fontSize: '18px', 
                 lineHeight: '1.8',
                 textIndent: index === 0 ? '0' : '2rem', // First paragraph no indent
                 marginBottom: '1.5rem'
             }}>
              {paragraph}
          </p>
      ));
  }

  // Separate component for rendering markdown to avoid hooks violations
  const MarkdownRenderer: React.FC<{ markdown: string }> = ({ markdown }) => {
    const [htmlContent, setHtmlContent] = useState<string>('');

    useEffect(() => {
      const convertMarkdown = async () => {
        try {
          const html = await markdownToHtml(markdown);
          setHtmlContent(html);
        } catch (error) {
          console.error('Error converting markdown to HTML:', error);
          // Fallback: display the raw markdown
          setHtmlContent(markdown);
        }
      };

      convertMarkdown();
    }, [markdown]);

    return (
      <div
        className="prose max-w-none book-toc"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  // Renders Markdown with links/lists for things like the Table of Contents
  const renderMarkdown = (md: string) => {
    if (!md) return null;
    return <MarkdownRenderer markdown={md} />;
  };

 const handleBuild = async () => {
    setIsBuilding(true);
    setBuildOutput(null);
    try {
      const markdown = projectStateToMarkdown(enhancedProjectState, selectedSections as any);
      setBuildOutput("Build successful! Generated Markdown:\n\n```markdown\n" + markdown.substring(0, 2000) + "...\n```"); // Show a larger snippet

    } catch (error) {
      setBuildOutput(`Build failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsBuilding(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const title = getProjectTitle(dbProject, 'My Work');

      if (exportFormat === 'PDF' || exportFormat === 'BOTH') {
        const pdfBlob = await generatePdf(enhancedProjectState, selectedSections, includeBackground);
        downloadFile(pdfBlob, `${title}.pdf`);
      }

      if (exportFormat === 'EPUB' || exportFormat === 'BOTH') {
        const epubBlob = await generateEpub(enhancedProjectState, selectedSections, includeBackground);
        downloadFile(epubBlob, `${title}.epub`);
      }

      if (exportFormat === 'KINDLE') {
        // Use the Kindle export utility which handles download automatically
        await generateAndDownloadKindleEpub(enhancedProjectState, selectedSections);
      }

      alert('Export completed successfully!');
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const downloadFile = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleGenerateTableOfContents = () => {
    const pageStructure = calculateBookPageStructure(projectState);
    const generatedToC = generateTableOfContents(pageStructure);
    
    // Update the project state with the generated ToC
    // Note: This would need to be handled by the parent component or a context
    // For now, we'll show it in a modal or copy to clipboard
    navigator.clipboard.writeText(generatedToC).then(() => {
      alert('Table of Contents copied to clipboard!');
    }).catch(() => {
      // Fallback: show in alert
      alert(`Generated Table of Contents:\n\n${generatedToC}`);
    });
  };

  return (
    <div className="flex flex-col h-full" style={{backgroundImage: "url('/assets/texture_tile.png')", backgroundRepeat: 'repeat', backgroundSize: 'auto'}}>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 gap-4">
            <h2 className="text-xl lg:text-2xl font-semibold">Book Preview & Export</h2>
            {/* Mobile-responsive controls */}
             <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="exportChapterContentPreference" className="text-gray-700 text-sm">Chapters:</label>
                  <select
                    id="exportChapterContentPreference"
                    value={enhancedProjectState.exportChapterContentPreference}
                    onChange={(e) => handleExportChapterContentPreferenceChange(e.target.value as 'preview' | 'content')}
                    className="p-2 border rounded text-sm flex-1 sm:flex-none"
                  >
                    <option value="content">Full Content</option>
                    <option value="preview">Preview Text</option>
                  </select>
                </div>

                <button onClick={handleBuild} disabled={isBuilding} className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-green-300 text-sm whitespace-nowrap">
                  {isBuilding ? 'Building...' : 'Build Preview'}
                </button>

                <button onClick={handleGenerateTableOfContents} className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 text-sm whitespace-nowrap">
                  Generate ToC
                </button>

              <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value as 'PDF' | 'EPUB' | 'KINDLE' | 'BOTH')} className="p-2 border rounded text-sm">
                <option value="PDF">PDF</option>
                <option value="EPUB">EPUB</option>
                <option value="KINDLE">📚 Kindle (KDP)</option>
                <option value="BOTH">PDF & EPUB</option>
              </select>
              
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeBackground}
                  onChange={(e) => setIncludeBackground(e.target.checked)}
                  className="rounded"
                />
                Include paper texture
              </label>
              
              <button onClick={handleExport} disabled={isExporting} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300 text-sm whitespace-nowrap">
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            </div>
        </div>
         {buildOutput && (
            <div className="mb-4 p-4 bg-gray-200 rounded-lg overflow-auto max-h-40 text-sm">
                <h4 className="font-semibold mb-2">Build Output:</h4>
                <pre className="whitespace-pre-wrap">{buildOutput}</pre>
            </div>
        )}

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Nav - Hidden on mobile, collapsible on tablet */}
        <div className="hidden lg:block lg:w-64 border-r bg-gray-50 p-4 overflow-y-auto">
             <div className="mb-6">
                 <div className="flex items-center justify-between mb-3">
                   <h3 className="font-semibold">Sections</h3>
                   {isSavingSections && (
                     <div className="flex items-center text-xs text-gray-500">
                       <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin mr-1"></div>
                       Saving...
                     </div>
                   )}
                 </div>
                 
                 {/* Master Controls */}
                 <div className="flex gap-2 mb-4">
                   <button
                     onClick={handleSelectAllSections}
                     className="flex-1 bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600 transition-colors"
                   >
                     Select All
                   </button>
                   <button
                     onClick={handleDeselectAllSections}
                     className="flex-1 bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 transition-colors"
                   >
                     Deselect All
                   </button>
                 </div>
                 
                 <div className="space-y-4">
                   {/* Front Matter Section */}
                   <div className="border rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <label className="flex items-center gap-2 font-medium">
                         <input
                           type="checkbox"
                           checked={selectedSections.frontMatter.enabled}
                           onChange={(e) => setSelectedSections({
                             ...selectedSections,
                             frontMatter: { ...selectedSections.frontMatter, enabled: e.target.checked }
                           })}
                         />
                         <span className="text-sm">📄 Front Matter</span>
                         <span className="text-xs text-gray-500">
                           ({getAvailableFrontMatterSections().length} available)
                         </span>
                       </label>
                       <button
                         onClick={() => toggleSectionExpansion('frontMatter')}
                         className="text-gray-500 hover:text-gray-700 text-sm"
                       >
                         {expandedSections.frontMatter ? '⌄' : '⌃'}
                       </button>
                     </div>
                     {expandedSections.frontMatter && (
                       <div className="ml-6 grid grid-cols-1 gap-2 text-xs">
                         {getAvailableFrontMatterSections().length > 0 ? (
                           getAvailableFrontMatterSections().map(({ key, label }) => (
                             <label key={key} className="flex items-center gap-1">
                               <input
                                 type="checkbox"
                                 checked={selectedSections.frontMatter[key as keyof typeof selectedSections.frontMatter]}
                                 onChange={(e) => setSelectedSections({
                                   ...selectedSections,
                                   frontMatter: {
                                     ...selectedSections.frontMatter,
                                     [key]: e.target.checked
                                   }
                                 })}
                               />
                               <span>✅ {label}</span>
                             </label>
                           ))
                         ) : (
                           <div className="text-gray-500 italic text-xs p-2">
                             No front matter content generated yet.
                             <br />
                             Generate content in Book Structure Studio first.
                           </div>
                         )}
                       </div>
                     )}
                   </div>

                   {/* Chapters Section */}
                   <div className="border rounded-lg p-3">
                     <label className="flex items-center gap-2 font-medium">
                       <input 
                         type="checkbox" 
                         checked={selectedSections.chapters.enabled}
                         onChange={(e) => setSelectedSections({
                           ...selectedSections, 
                           chapters: { ...selectedSections.chapters, enabled: e.target.checked }
                         })}
                       />
                       <span className="text-sm">📚 Chapters</span>
                     </label>
                   </div>

                   {/* Back Matter Section */}
                   <div className="border rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <label className="flex items-center gap-2 font-medium">
                         <input
                           type="checkbox"
                           checked={selectedSections.backMatter.enabled}
                           onChange={(e) => setSelectedSections({
                             ...selectedSections,
                             backMatter: { ...selectedSections.backMatter, enabled: e.target.checked }
                           })}
                         />
                         <span className="text-sm">📋 Back Matter</span>
                         <span className="text-xs text-gray-500">
                           ({getAvailableBackMatterSections().length} available)
                         </span>
                       </label>
                       <button
                         onClick={() => toggleSectionExpansion('backMatter')}
                         className="text-gray-500 hover:text-gray-700 text-sm"
                       >
                         {expandedSections.backMatter ? '⌄' : '⌃'}
                       </button>
                     </div>
                     {expandedSections.backMatter && (
                       <div className="ml-6 grid grid-cols-1 gap-2 text-xs">
                         {getAvailableBackMatterSections().length > 0 ? (
                           getAvailableBackMatterSections().map(({ key, label }) => (
                             <label key={key} className="flex items-center gap-1">
                               <input
                                 type="checkbox"
                                 checked={selectedSections.backMatter[key as keyof typeof selectedSections.backMatter]}
                                 onChange={(e) => setSelectedSections({
                                   ...selectedSections,
                                   backMatter: {
                                     ...selectedSections.backMatter,
                                     [key]: e.target.checked
                                   }
                                 })}
                               />
                               <span>✅ {label}</span>
                             </label>
                           ))
                         ) : (
                           <div className="text-gray-500 italic text-xs p-2">
                             No back matter content generated yet.
                             <br />
                             Generate content in Book Structure Studio first.
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                 </div>
             </div>
             <h3 className="font-semibold mb-4">Chapters</h3>
             <div className="space-y-1">
               {enhancedProjectState.chapters.map((chapter, index) => (
                 <a key={chapter.id} href={`#chapter-${index}`} className="block p-2 text-sm hover:bg-gray-100 rounded">
                   Chapter {index + 1}: {chapter.title || 'Untitled'}
                 </a>
               ))}
             </div>
        </div>

        {/* Main Content - Traditional Book Page */}
        <div className="flex-1 overflow-auto bg-amber-50" style={{
            backgroundImage: "url('/assets/texture_tile.png')", 
            backgroundRepeat: 'repeat', 
            backgroundSize: 'auto',
            backgroundColor: '#fefdf9'
        }}>
          <div className="book-page max-w-4xl mx-auto" style={{
              fontFamily: "'Crimson Text', 'Times New Roman', serif", 
              fontSize: '16px', 
              lineHeight: '1.7', 
              color: '#2c1810',
              backgroundColor: 'rgba(254, 253, 249, 0.98)', 
              padding: '2rem 1rem 2rem 1rem', 
              margin: '1rem auto',
              borderRadius: '2px',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              border: '1px solid rgba(139, 92, 70, 0.1)',
              minHeight: '900px',
              position: 'relative'
          }}>
          <style>{`
            @media (min-width: 768px) {
              .book-page {
                padding: 3rem 2rem 3rem 2rem !important;
                margin: 1.5rem auto !important;
              }
            }
            @media (min-width: 1024px) {
              .book-page {
                padding: 5rem 4rem 4rem 4rem !important;
                margin: 2rem auto !important;
              }
            }
            .book-toc ul, .book-toc ol { 
              margin: 0 0 1rem 1.25rem; 
              font-family: 'Crimson Text', 'Times New Roman', serif;
            }
            .book-toc li { 
              margin: 0.25rem 0; 
              line-height: 1.6;
            }
            .book-toc a {
              color: #2c1810;
              text-decoration: none;
              border-bottom: 1px dotted #8b5c46;
            }
            .book-toc a:hover {
              color: #5d4e37;
              border-bottom-color: #5d4e37;
            }
          `}</style>
            {/* Page Header - Mobile responsive */}
            <div className="page-header hidden md:flex" style={{
                position: 'absolute',
                top: '2rem',
                left: '2rem',
                right: '2rem',
                height: '2rem',
                borderBottom: '1px solid rgba(139, 92, 70, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.75rem',
                color: '#8b5c46',
                fontStyle: 'italic'
            }}>
              {/* BLUEPRINT MIGRATION: Use blueprint.worldBuildingSuggestions.selectedStoryTitle */}
              <span className="truncate">{enhancedProjectState.blueprint?.worldBuildingSuggestions?.selectedStoryTitle || 'My Work'}</span>
              <span className="truncate">{enhancedProjectState.authorDetails?.authorName || 'Author'}</span>
            </div>

            {/* Page Footer - Mobile responsive */}
            <div className="page-footer hidden md:flex" style={{
                position: 'absolute',
                bottom: '2rem',
                left: '2rem',
                right: '2rem',
                height: '1.5rem',
                borderTop: '1px solid rgba(139, 92, 70, 0.1)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '0.75rem',
                color: '#8b5c46',
                paddingTop: '0.5rem'
            }}>
              <span>~ {new Date().getFullYear()} ~</span>
            </div>
            {/* --- Render Front Matter --- */}
            {selectedSections.frontMatter.enabled && (
              <>
                <h1 className="book-title text-2xl md:text-4xl lg:text-5xl" style={{
                    fontFamily: "'Crimson Text', 'Times New Roman', serif", 
                    fontWeight: '600', 
                    color: '#2c1810', 
                    textAlign: 'center',
                    marginBottom: '0.5rem',
                    letterSpacing: '0.05em',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>{/* BLUEPRINT MIGRATION: Use blueprint.worldBuildingSuggestions.selectedStoryTitle */}{enhancedProjectState.blueprint?.worldBuildingSuggestions?.selectedStoryTitle || 'My Work'}</h1>
                <h2 className="book-author text-lg md:text-xl lg:text-2xl" style={{
                    fontFamily: "'Crimson Text', 'Times New Roman', serif", 
                    fontStyle: 'italic', 
                    color: '#5d4e37', 
                    textAlign: 'center',
                    marginBottom: '2rem lg:mb-12',
                    fontWeight: '400'
                }}>By {enhancedProjectState.authorDetails?.authorName || 'Author'}</h2>
                {selectedSections.frontMatter.titlePageContent && enhancedProjectState.frontMatter?.titlePageImageBase64 && <CoverImageWithFallback src={enhancedProjectState.frontMatter.titlePageImageBase64} alt="Title" className="mx-auto" fallbackClassName="mx-auto w-full max-w-sm" />}
                {selectedSections.frontMatter.titlePageContent && renderContent(enhancedProjectState.frontMatter?.titlePageContent)}
                {selectedSections.frontMatter.copyrightPageContent && enhancedProjectState.frontMatter?.copyrightPageContent && <><h3>Copyright</h3>{renderContent(enhancedProjectState.frontMatter.copyrightPageContent)}</>}
                {selectedSections.frontMatter.dedication && enhancedProjectState.frontMatter?.dedication && <><h3>Dedication</h3>{renderContent(enhancedProjectState.frontMatter.dedication)}</>}
                {selectedSections.frontMatter.epigraph && enhancedProjectState.frontMatter?.epigraph && <><h3>Epigraph</h3>{renderContent(enhancedProjectState.frontMatter.epigraph)}</>}
                {selectedSections.frontMatter.tableOfContents && enhancedProjectState.frontMatter?.tableOfContents && enhancedProjectState.frontMatter.tableOfContents !== '<!-- Table of Contents will be generated here -->' && <><h3>Table of Contents</h3>{renderMarkdown(enhancedProjectState.frontMatter.tableOfContents)}</>}
                {selectedSections.frontMatter.foreword && enhancedProjectState.frontMatter?.foreword && <><h3>Foreword</h3>{renderContent(enhancedProjectState.frontMatter.foreword)}</>}
                {selectedSections.frontMatter.preface && enhancedProjectState.frontMatter?.preface && <><h3>Preface</h3>{renderContent(enhancedProjectState.frontMatter.preface)}</>}
                {selectedSections.frontMatter.acknowledgmentsFront && enhancedProjectState.frontMatter?.acknowledgmentsFront && <><h3>Acknowledgments</h3>{renderContent(enhancedProjectState.frontMatter.acknowledgmentsFront)}</>}
                {selectedSections.frontMatter.prologue && enhancedProjectState.frontMatter?.prologue && <><h3>Prologue</h3>{renderContent(enhancedProjectState.frontMatter.prologue)}</>}
                {selectedSections.frontMatter.introduction && enhancedProjectState.frontMatter?.introduction && <><h3>Introduction</h3>{renderContent(enhancedProjectState.frontMatter.introduction)}</>}
              </>
            )}

            {/* --- Render Chapters with Progressive Loading --- */}
            {selectedSections.chapters.enabled && enhancedProjectState.chapters.map((chapter, index) => {
              const content = getProgressiveChapterContent(chapter);
              const isLoading = loadingChapters.has(chapter.id);
              const canShowMore = hasMoreContent(chapter);
              
              return (
                <div key={chapter.id} id={`chapter-${index}`} className="mb-8 md:mb-12" style={{pageBreakBefore: 'always'}}>
                  <div className="chapter-header" style={{textAlign: 'center', marginBottom: '2rem md:mb-12', paddingBottom: '1rem', borderBottom: '1px solid rgba(139, 92, 70, 0.2)'}}>
                    <div className="text-xs md:text-sm" style={{
                        fontFamily: "'Crimson Text', 'Times New Roman', serif", 
                        fontWeight: '500', 
                        color: '#8b5c46', 
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        marginBottom: '0.5rem'
                    }}>Chapter {index + 1}</div>
                    <h3 className="text-xl md:text-2xl lg:text-3xl" style={{
                        fontFamily: "'Crimson Text', 'Times New Roman', serif", 
                        fontWeight: '600', 
                        color: '#2c1810',
                        marginBottom: '0',
                        lineHeight: '1.3'
                    }}>{chapter.title || 'Untitled Chapter'}</h3>
                  </div>
                  
                  <div className="chapter-content text-sm md:text-base">
                    {renderContent(content)}
                    
                    {/* Show More Button */}
                    {canShowMore && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={() => loadFullChapterContent(chapter)}
                          disabled={isLoading}
                          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                          style={{
                            fontFamily: "'Inter', 'System UI', sans-serif"
                          }}
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Loading full content...</span>
                            </div>
                          ) : (
                            'Show More'
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* Content loaded indicator */}
                    {expandedChapters.has(chapter.id) && (
                      <div className="mt-4 text-center">
                        <span className="text-xs text-gray-500 italic">
                          Full chapter content loaded
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* --- Render Back Matter --- */}
            {selectedSections.backMatter.enabled && (
                <>
                    {selectedSections.backMatter.epilogue && enhancedProjectState.backMatter?.epilogue && <><h3>Epilogue</h3>{renderContent(enhancedProjectState.backMatter.epilogue)}</>}
                    {selectedSections.backMatter.afterword && enhancedProjectState.backMatter?.afterword && <><h3>Afterword</h3>{renderContent(enhancedProjectState.backMatter.afterword)}</>}
                    {selectedSections.backMatter.authorsNote && enhancedProjectState.backMatter?.authorsNote && <><h3>Author's Note</h3>{renderContent(enhancedProjectState.backMatter.authorsNote)}</>}
                    {selectedSections.backMatter.acknowledgmentsBack && enhancedProjectState.backMatter?.acknowledgmentsBack && <><h3>Acknowledgments</h3>{renderContent(enhancedProjectState.backMatter.acknowledgmentsBack)}</>}
                    {selectedSections.backMatter.appendices && enhancedProjectState.backMatter?.appendices && enhancedProjectState.backMatter.appendices.length > 0 &&
                        <><h3>Appendices</h3>{enhancedProjectState.backMatter.appendices.map((a: any) => <div key={a.id}><h4>{a.title}</h4>{renderContent(a.content)}</div>)}</>
                    }
                    {selectedSections.backMatter.glossary && enhancedProjectState.backMatter?.glossary && enhancedProjectState.backMatter.glossary.length > 0 &&
                        <><h3>Glossary</h3>{enhancedProjectState.backMatter.glossary.map((g: any) => <p key={g.id}><strong>{g.term}:</strong> {g.definition}</p>)}</>
                    }
                    {selectedSections.backMatter.bibliography && enhancedProjectState.backMatter?.bibliography && enhancedProjectState.backMatter.bibliography.length > 0 &&
                        <><h3>Bibliography</h3>{enhancedProjectState.backMatter.bibliography.map((b: any) => <p key={b.id}>{b.citation}</p>)}</>
                    }
                    {selectedSections.backMatter.index && enhancedProjectState.backMatter?.index && <><h3>Index</h3>{renderContent(enhancedProjectState.backMatter.index)}</>}
                    {selectedSections.backMatter.readingGroupGuide && enhancedProjectState.backMatter?.readingGroupGuide && <><h3>Reading Group Guide</h3>{renderContent(enhancedProjectState.backMatter.readingGroupGuide)}</>}
                    {selectedSections.backMatter.teaserNextBook && enhancedProjectState.backMatter?.teaserNextBook && <><h3>Teaser for Next Book</h3>{renderContent(enhancedProjectState.backMatter.teaserNextBook)}</>}
                     {selectedSections.backMatter.aboutTheAuthor && generateAboutTheAuthorSection(enhancedProjectState.authorDetails) &&
                        <><h3>About the Author</h3>{renderContent(generateAboutTheAuthorSection(enhancedProjectState.authorDetails))}</>
                    }
                    {selectedSections.backMatter.aboutTheAuthor && enhancedProjectState.authorDetails?.authorProfileImageBase64 && <CoverImageWithFallback src={enhancedProjectState.authorDetails.authorProfileImageBase64} alt="Author" className="max-w-xs rounded" fallbackClassName="max-w-xs" />}
                    {selectedSections.backMatter.contactSocialMedia && generateContactSocialMediaSection(enhancedProjectState.authorDetails) && <><h3>Contact / Social Media</h3>{renderContent(generateContactSocialMediaSection(enhancedProjectState.authorDetails))}</>}
                    {enhancedProjectState.backMatter?.backCoverImageBase64 && <CoverImageWithFallback src={enhancedProjectState.backMatter.backCoverImageBase64} alt="Back Cover" className="w-full" fallbackClassName="w-full" />}
                </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPreview;